import {
  type Form,
  type FieldsInputField,
  InputFieldInputType,
  type Format,
} from '@wix/ambassador-forms-v4-form/types';

import { type Submission } from '../../submission';
import { type ValidationResult } from '../submission-validation/errors';
import { type FormValidator } from '../submission-validation/form-validator';
import { parseIfNeeded } from '../submission-validation/mappers/string-format-options-mapper';

import { getDefaultValue } from './default-value-utils';

export class FormSchemaValidator {
  constructor(private readonly submissionValidator: FormValidator) {}

  validate(form: Form, timezone?: string): ValidationResult {
    const submission =
      form.formFields?.reduce((acc, { inputOptions }) => {
        const defaultValue = getDefaultValue(inputOptions);
        const target = inputOptions?.target;
        if (defaultValue && target) {
          acc[target] = this.buildSubmissionValue(
            defaultValue,
            inputOptions,
            timezone,
          );
        }
        return acc;
      }, {} as Submission) || {};

    if (Object.keys(submission).length) {
      return this.submissionValidator.validate(form.id!, submission);
    }
    return { errors: [] };
  }

  private buildSubmissionValue(
    defaultValue: string | number,
    inputField: FieldsInputField,
    timezone?: string,
  ) {
    switch (inputField.inputType) {
      case InputFieldInputType.PAYMENT:
        return inputField.paymentOptions?.validation?.products?.map(
          (product) => ({
            productId: product.id,
            price: defaultValue,
            quantity: 1,
          }),
        );
      case InputFieldInputType.STRING: {
        const format = inputField.stringOptions?.validation?.format as
          | Format
          | undefined;
        if (!format || typeof defaultValue !== 'string') {
          return defaultValue;
        }
        return (
          parseIfNeeded(format, () => new Date(), defaultValue, timezone) ??
          defaultValue
        );
      }
      default:
        return defaultValue;
    }
  }
}
